// Structured editor for a provider's postback config. It reads and writes the same JSON the
// "Advanced" textarea shows, so the two never disagree: the JSON text stays the source of truth.
import { useId, useState } from 'react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { Check, Copy, Eye, KeyRound, Plus, TriangleAlert, Trash2 } from 'lucide-react';
import { allowlistEntryError, splitAllowlist } from '@gaintplay/core/ip';
import { Field, JsonError, RequiredTag } from './WallForm';

type Json = Record<string, unknown>;
interface Fields {
  userId?: string;
  userIdIs?: 'user' | 'click';
  txid?: string;
  amount?: string;
  amountIs?: 'coins' | 'usd' | 'usd_cents';
  amountFallback?: 'payout';
  revenueUsd?: string;
  offerId?: string;
  offerName?: string;
  eventId?: string;
  eventName?: string;
  ip?: string;
  country?: string;
  clickId?: string;
  extra?: Record<string, string>;
}
interface Pb {
  path?: string;
  methods?: string[];
  source?: string;
  auth?: { key?: { location: string; name: string; value: string }; ipAllowlist?: string[] } & Json;
  fields?: Fields;
  reversal?: {
    statusField?: string;
    creditValues?: string[];
    reversalValues?: string[];
    negativeAmount?: boolean;
  };
  ban?: {
    field?: string;
    values?: string[];
    ignoreValues?: string[];
    /** The webhook's own signing secret when it differs from the postbacks' (AdGem's token). */
    secret?: string;
    reasonField?: string;
    atField?: string;
  };
  response?: { body?: string; errorBody?: string; contentType?: string; status?: number };
  macros?: Record<string, string>;
}

const ROWS: { key: keyof Fields; label: string; hint: string; required?: boolean }[] = [
  {
    key: 'userId',
    label: 'User ID',
    hint: 'Our id, sent in the wall URL as {user_id}',
    required: true,
  },
  {
    key: 'txid',
    label: 'Transaction ID',
    hint: 'Unique per conversion; stops double credits',
    required: true,
  },
  { key: 'amount', label: 'Reward amount', hint: 'What the user earns', required: true },
  { key: 'revenueUsd', label: 'Payout to us (USD)', hint: 'What the network pays GaintPlay' },
  { key: 'offerId', label: 'Offer ID', hint: 'The network’s id for the offer' },
  { key: 'offerName', label: 'Offer name', hint: 'Shown in the wallet and live feed' },
  { key: 'eventId', label: 'Event ID', hint: 'Multi-event offers: which step paid' },
  { key: 'eventName', label: 'Event name', hint: 'e.g. “Castle level 7”' },
  { key: 'ip', label: 'User IP', hint: 'Compared with the user’s own IPs' },
  { key: 'country', label: 'Country', hint: 'Two-letter code, e.g. US' },
  { key: 'clickId', label: 'Click ID', hint: 'Only if you send {click_id} separately' },
];

const list = (v: string) =>
  v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/** Plain-English summary of how the current status/amount settings decide credit vs take-back. */
function reversalSummary(r: NonNullable<Pb['reversal']>): string {
  const q = (xs: string[]) => xs.map((x) => `“${x}”`).join(' or ');
  const cred = r.creditValues ?? [];
  const rev = r.reversalValues ?? [];
  const parts: string[] = [];
  if (r.statusField && (cred.length || rev.length)) {
    if (cred.length) parts.push(`${r.statusField} = ${q(cred)} credits`);
    if (rev.length) parts.push(`${q(rev)} takes back`);
    if (cred.length && rev.length) parts.push('anything else is logged and ignored');
    else if (cred.length) parts.push('anything else takes back');
    else parts.push('anything else credits');
  }
  if (r.negativeAmount) parts.push('a negative amount always takes back');
  if (!parts.length)
    return 'Every postback credits. Set a status or negative amount to handle take-backs.';
  return `${parts.join('; ')}. Matching ignores case.`;
}

function randomKey(): string {
  const a = crypto.getRandomValues(new Uint8Array(18));
  return btoa(String.fromCharCode(...a))
    .replace(/[+/=]/g, '')
    .slice(0, 24);
}

export function PostbackForm({
  text,
  onText,
  base,
  providerId,
  saved,
}: {
  text: string;
  onText: (t: string) => void;
  base: string;
  providerId: string;
  /** True when editing a provider that already exists, so its stored key can be revealed. */
  saved: boolean;
}) {
  const [newExtra, setNewExtra] = useState({ label: '', ref: '' });
  const uid = useId();
  // The key and the allowed IPs decide who may send postbacks: providers.secrets.
  const canSecrets = useCan()('providers.secrets');
  // Raw text so typing "1.2.3.4, " isn't reformatted mid-keystroke; the JSON gets the parsed list.
  const [ipText, setIpText] = useState(() => {
    try {
      return ((JSON.parse(text) as Pb).auth?.ipAllowlist ?? []).join('\n');
    } catch {
      return '';
    }
  });
  let pb: Pb;
  try {
    pb = JSON.parse(text) as Pb;
  } catch {
    return (
      <JsonError>
        The postback JSON doesn’t parse, so this form is paused. Fix it under “Edit as JSON” below.
      </JsonError>
    );
  }
  const fields = pb.fields ?? {};
  const rev = pb.reversal ?? {};
  const ban = pb.ban ?? {};
  const key = pb.auth?.key;
  const update = (fn: (draft: Pb) => void) => {
    const draft = structuredClone(pb);
    draft.fields ??= {};
    fn(draft);
    // Drop empty optional values so the stored config stays tidy.
    for (const [k, v] of Object.entries(draft.fields))
      if (v === '' || (k === 'extra' && v && !Object.keys(v).length))
        delete (draft.fields as Json)[k];
    if (draft.reversal)
      for (const [k, v] of Object.entries(draft.reversal))
        if (v === '' || (Array.isArray(v) && !v.length)) delete (draft.reversal as Json)[k];
    if (draft.response && !draft.response.errorBody) delete draft.response.errorBody;
    if (draft.ban) {
      for (const [k, v] of Object.entries(draft.ban))
        if (v === '' || (Array.isArray(v) && !v.length)) delete (draft.ban as Json)[k];
      if (!Object.keys(draft.ban).length) delete draft.ban;
    }
    onText(JSON.stringify(draft, null, 2));
  };
  const path = pb.path || providerId || '<id>';
  const keyWhere = `${key?.location === 'header' ? 'header' : 'query parameter'} “${key?.name ?? 'key'}”`;

  return (
    <div className="space-y-6">
      {/* ---- Where the network calls, and how it proves who it is ---- */}
      <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
        <Field id={`${uid}-path`} label="Postback path" hint="Leave blank to use the ID.">
          <input
            id={`${uid}-path`}
            className="input h-11 py-0 font-mono text-[14px]"
            placeholder={providerId || 'same as the ID'}
            value={pb.path ?? ''}
            spellCheck={false}
            onChange={(e) => update((d) => (d.path = e.target.value))}
          />
        </Field>
        <Field
          id={`${uid}-key`}
          label="Shared key"
          hint={
            canSecrets
              ? `A secret the network sends as the ${keyWhere}, so we know the call is really from them.`
              : 'Changing the key or the allowed IPs needs “Reveal network keys”.'
          }
        >
          <div className="flex gap-2">
            <input
              id={`${uid}-key`}
              className="input h-11 py-0 font-mono text-[13px]"
              value={key?.value ?? ''}
              placeholder="Paste or generate"
              spellCheck={false}
              autoComplete="off"
              disabled={!canSecrets}
              onChange={(e) =>
                update((d) => {
                  d.auth ??= {};
                  d.auth.key = {
                    location: key?.location ?? 'query',
                    name: key?.name ?? 'key',
                    value: e.target.value,
                  };
                })
              }
            />
            <button
              type="button"
              className="btn-ghost h-11 w-11 shrink-0 p-0"
              title="Generate a new key"
              aria-label="Generate a new key"
              disabled={!canSecrets}
              onClick={() =>
                update((d) => {
                  d.auth ??= {};
                  d.auth.key = {
                    location: key?.location ?? 'query',
                    name: key?.name ?? 'key',
                    value: randomKey(),
                  };
                })
              }
            >
              <KeyRound size={16} aria-hidden="true" />
            </button>
          </div>
        </Field>
      </div>

      <IpAllowlist
        id={`${uid}-ips`}
        text={ipText}
        hasKey={!!key?.value}
        disabled={!canSecrets}
        onText={(t) => {
          setIpText(t);
          update((d) => {
            d.auth ??= {};
            const list = splitAllowlist(t);
            if (list.length) d.auth.ipAllowlist = list;
            else delete d.auth.ipAllowlist;
          });
        }}
      />

      {/* ---- Field mapping ---- */}
      <div>
        <h4 className="text-[15px] font-bold">Field mapping</h4>
        <p className="mt-1 max-w-prose text-[13px] leading-relaxed text-stone">
          For each value, the name of the parameter the network sends. Only the first three are
          required; leave the rest blank if the network doesn’t send them. Use{' '}
          <code className="rounded bg-ink/[0.05] px-1 text-graphite">a|b</code> for alternatives and{' '}
          <code className="rounded bg-ink/[0.05] px-1 text-graphite">data.user.id</code> for nested
          JSON.
        </p>
        <div className="mt-3 divide-y divide-hairline rounded-[14px] border border-hairline bg-paper">
          {ROWS.map((r) => {
            const id = `${uid}-f-${r.key}`;
            return (
              <div
                key={r.key}
                className="grid items-center gap-x-4 gap-y-2 px-3.5 py-3 sm:grid-cols-[190px_minmax(0,1fr)] sm:px-4"
              >
                <div className="min-w-0">
                  <label
                    htmlFor={id}
                    className="flex flex-wrap items-center gap-1.5 text-[14px] font-semibold"
                  >
                    {r.label}
                    {r.required && <RequiredTag />}
                  </label>
                  <div className="mt-0.5 text-[12px] leading-snug text-stone">{r.hint}</div>
                </div>
                <div className="flex min-w-0 gap-2">
                  <input
                    id={id}
                    className="input h-10 min-w-0 py-0 font-mono text-[13px]"
                    placeholder={r.required ? 'Required' : 'Not sent'}
                    spellCheck={false}
                    aria-required={r.required || undefined}
                    value={(fields[r.key] as string | undefined) ?? ''}
                    onChange={(e) =>
                      update((d) => ((d.fields as Json)[r.key] = e.target.value.trim()))
                    }
                  />
                  {r.key === 'amount' && (
                    <select
                      className="input h-10 w-[132px] shrink-0 py-0 text-[13px]"
                      aria-label="The reward amount is"
                      value={fields.amountIs ?? 'coins'}
                      onChange={(e) =>
                        update((d) => {
                          if (e.target.value === 'coins') delete d.fields!.amountIs;
                          else d.fields!.amountIs = e.target.value as 'usd' | 'usd_cents';
                        })
                      }
                    >
                      <option value="coins">in coins</option>
                      <option value="usd">in USD</option>
                      <option value="usd_cents">in US cents</option>
                    </select>
                  )}
                  {r.key === 'userId' && (
                    <select
                      className="input h-10 w-[132px] shrink-0 py-0 text-[13px]"
                      aria-label="This value is"
                      value={fields.userIdIs ?? 'user'}
                      onChange={(e) =>
                        update((d) => {
                          if (e.target.value === 'user') delete d.fields!.userIdIs;
                          else d.fields!.userIdIs = 'click';
                        })
                      }
                    >
                      <option value="user">is the user id</option>
                      <option value="click">is a click id</option>
                    </select>
                  )}
                </div>
                {r.key === 'amount' && (
                  <label className="flex items-start gap-2 text-[13px] leading-snug text-graphite sm:col-start-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-ink"
                      checked={fields.amountFallback === 'payout'}
                      onChange={(e) =>
                        update((d) => {
                          if (e.target.checked) d.fields!.amountFallback = 'payout';
                          else delete d.fields!.amountFallback;
                        })
                      }
                    />
                    <span>
                      When the amount is empty, pay the payout instead (“Payout to us”, in USD, at
                      this network’s coins per $1). EdgeCash sends no amount for offers read through
                      its API.
                    </span>
                  </label>
                )}
              </div>
            );
          })}

          {/* Status / reversals */}
          <div className="grid items-start gap-x-4 gap-y-2 px-3.5 py-3 sm:grid-cols-[190px_minmax(0,1fr)] sm:px-4">
            <div>
              <div className="text-[14px] font-semibold">Credit or take back</div>
              <div className="mt-0.5 text-[12px] leading-snug text-stone">
                How we tell a new reward from a reversal: status values, a negative amount, or both
              </div>
            </div>
            <div className="min-w-0 space-y-2.5">
              <div className="grid gap-2 sm:grid-cols-3">
                <MiniField id={`${uid}-st`} label="Status parameter">
                  <input
                    id={`${uid}-st`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="e.g. status"
                    spellCheck={false}
                    value={rev.statusField ?? ''}
                    onChange={(e) =>
                      update((d) => ((d.reversal ??= {}).statusField = e.target.value.trim()))
                    }
                  />
                </MiniField>
                <MiniField id={`${uid}-cr`} label="Credits when">
                  <input
                    id={`${uid}-cr`}
                    className="input h-10 py-0 text-[13px]"
                    placeholder="e.g. 1, completed"
                    value={(rev.creditValues ?? []).join(', ')}
                    onChange={(e) =>
                      update((d) => ((d.reversal ??= {}).creditValues = list(e.target.value)))
                    }
                  />
                </MiniField>
                <MiniField id={`${uid}-tb`} label="Takes back when">
                  <input
                    id={`${uid}-tb`}
                    className="input h-10 py-0 text-[13px]"
                    placeholder="e.g. 2, reversed"
                    value={(rev.reversalValues ?? []).join(', ')}
                    onChange={(e) =>
                      update((d) => ((d.reversal ??= {}).reversalValues = list(e.target.value)))
                    }
                  />
                </MiniField>
              </div>
              <label className="inline-flex min-h-9 cursor-pointer items-center gap-2.5 text-[13px] text-graphite">
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 cursor-pointer accent-ink"
                  checked={!!rev.negativeAmount}
                  onChange={(e) =>
                    update((d) => {
                      d.reversal ??= {};
                      if (e.target.checked) d.reversal.negativeAmount = true;
                      else delete d.reversal.negativeAmount;
                    })
                  }
                />
                A negative amount means take back
              </label>
              <p className="rounded-[10px] bg-canvas px-3 py-2 text-[13px] leading-snug text-graphite">
                <span className="font-semibold text-ink">In short: </span>
                {reversalSummary(rev)}
              </p>
            </div>
          </div>

          {/* Ban events */}
          <div className="grid items-start gap-x-4 gap-y-2 px-3.5 py-3 sm:grid-cols-[190px_minmax(0,1fr)] sm:px-4">
            <div>
              <div className="text-[14px] font-semibold">Ban events</div>
              <div className="mt-0.5 text-[12px] leading-snug text-stone">
                When the network tells us it banned a user. Optional; leave blank if it doesn’t
              </div>
            </div>
            <div className="min-w-0 space-y-2.5">
              <div className="grid gap-2 sm:grid-cols-2">
                <MiniField id={`${uid}-bf`} label="Event parameter">
                  <input
                    id={`${uid}-bf`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="e.g. data.event_type"
                    spellCheck={false}
                    value={ban.field ?? ''}
                    onChange={(e) => update((d) => ((d.ban ??= {}).field = e.target.value.trim()))}
                  />
                </MiniField>
                <MiniField id={`${uid}-bv`} label="Means banned when">
                  <input
                    id={`${uid}-bv`}
                    className="input h-10 py-0 text-[13px]"
                    placeholder="e.g. ban"
                    value={(ban.values ?? []).join(', ')}
                    onChange={(e) => update((d) => ((d.ban ??= {}).values = list(e.target.value)))}
                  />
                </MiniField>
                <MiniField id={`${uid}-bi`} label="Answer OK and ignore when (optional)">
                  <input
                    id={`${uid}-bi`}
                    className="input h-10 py-0 text-[13px]"
                    placeholder="e.g. player.unbanned"
                    value={(ban.ignoreValues ?? []).join(', ')}
                    onChange={(e) =>
                      update((d) => ((d.ban ??= {}).ignoreValues = list(e.target.value)))
                    }
                  />
                </MiniField>
                <MiniField id={`${uid}-br`} label="Reason parameter (optional)">
                  <input
                    id={`${uid}-br`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="e.g. data.ban_reason"
                    spellCheck={false}
                    value={ban.reasonField ?? ''}
                    onChange={(e) =>
                      update((d) => ((d.ban ??= {}).reasonField = e.target.value.trim()))
                    }
                  />
                </MiniField>
                <MiniField id={`${uid}-ba`} label="Time parameter (optional)">
                  <input
                    id={`${uid}-ba`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="e.g. data.at"
                    spellCheck={false}
                    value={ban.atField ?? ''}
                    onChange={(e) =>
                      update((d) => ((d.ban ??= {}).atField = e.target.value.trim()))
                    }
                  />
                </MiniField>
                <MiniField id={`${uid}-bs`} label="Webhook secret (optional)">
                  <input
                    id={`${uid}-bs`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder={
                      canSecrets
                        ? 'Only if it differs from the signature secret'
                        : 'Needs “Reveal network keys”'
                    }
                    spellCheck={false}
                    autoComplete="off"
                    disabled={!canSecrets}
                    value={ban.secret ?? ''}
                    onChange={(e) => update((d) => ((d.ban ??= {}).secret = e.target.value.trim()))}
                  />
                </MiniField>
              </div>
              <p className="rounded-[10px] bg-canvas px-3 py-2 text-[13px] leading-snug text-graphite">
                <span className="font-semibold text-ink">In short: </span>
                {ban.field && ban.values?.length
                  ? `When “${ban.field}” is ${ban.values.map((v) => `“${v}”`).join(' or ')}, the user’s account is blocked and everything it earned from this network is taken back. It passes the same checks as every postback, and counts once per user.${
                      ban.ignoreValues?.length
                        ? ` When it is ${ban.ignoreValues.map((v) => `“${v}”`).join(' or ')}, we answer OK and do nothing (unblocking stays with staff).`
                        : ''
                    }`
                  : 'Off: this network’s ban messages are refused like any postback we can’t read.'}
              </p>
            </div>
          </div>

          {/* Extras */}
          <div className="grid items-start gap-x-4 gap-y-2 px-3.5 py-3 sm:grid-cols-[190px_minmax(0,1fr)] sm:px-4">
            <div>
              <div className="text-[14px] font-semibold">Anything else</div>
              <div className="mt-0.5 text-[12px] leading-snug text-stone">
                Extra values kept on the conversion, e.g. multi_event
              </div>
            </div>
            <div className="min-w-0 space-y-2">
              {Object.entries(fields.extra ?? {}).length > 0 && (
                <ul className="space-y-1.5">
                  {Object.entries(fields.extra ?? {}).map(([label, ref]) => (
                    <li
                      key={label}
                      className="flex min-h-10 items-center gap-2 rounded-[10px] bg-canvas py-1 pl-3 pr-1 text-[13px]"
                    >
                      <span className="font-semibold">{label}</span>
                      <span className="text-stone" aria-label="from">
                        ←
                      </span>
                      <code className="min-w-0 truncate text-graphite">{ref}</code>
                      <button
                        type="button"
                        className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-[8px] text-stone transition hover:bg-berry/10 hover:text-berry-deep"
                        aria-label={`Remove ${label}`}
                        onClick={() => update((d) => delete d.fields!.extra![label])}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid items-end gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                <MiniField id={`${uid}-xl`} label="Name">
                  <input
                    id={`${uid}-xl`}
                    className="input h-10 py-0 text-[13px]"
                    placeholder="e.g. multiEvent"
                    value={newExtra.label}
                    onChange={(e) => setNewExtra({ ...newExtra, label: e.target.value })}
                  />
                </MiniField>
                <MiniField id={`${uid}-xr`} label="Parameter">
                  <input
                    id={`${uid}-xr`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="e.g. multi_event"
                    spellCheck={false}
                    value={newExtra.ref}
                    onChange={(e) => setNewExtra({ ...newExtra, ref: e.target.value })}
                  />
                </MiniField>
                <button
                  type="button"
                  className="btn-ghost h-10 shrink-0 px-3.5 py-0 text-[13px]"
                  disabled={!newExtra.label.trim() || !newExtra.ref.trim()}
                  onClick={() => {
                    update((d) => {
                      d.fields!.extra = {
                        ...(d.fields!.extra ?? {}),
                        [newExtra.label.trim()]: newExtra.ref.trim(),
                      };
                    });
                    setNewExtra({ label: '', ref: '' });
                  }}
                >
                  <Plus size={15} aria-hidden="true" /> Add
                </button>
              </div>
            </div>
          </div>

          {/* Our reply */}
          <div className="grid items-start gap-x-4 gap-y-2 px-3.5 py-3 sm:grid-cols-[190px_minmax(0,1fr)] sm:px-4">
            <div>
              <div className="text-[14px] font-semibold">Our reply</div>
              <div className="mt-0.5 text-[12px] leading-snug text-stone">
                What the network wants back. Its docs say, e.g. Lootably: 1 and 0
              </div>
            </div>
            <div className="min-w-0 space-y-2.5">
              <div className="grid gap-2 sm:grid-cols-2">
                <MiniField id={`${uid}-ok`} label="When we take it">
                  <input
                    id={`${uid}-ok`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="ok"
                    spellCheck={false}
                    maxLength={200}
                    value={pb.response?.body ?? ''}
                    onChange={(e) => update((d) => ((d.response ??= {}).body = e.target.value))}
                  />
                </MiniField>
                <MiniField id={`${uid}-no`} label="When we refuse it">
                  <input
                    id={`${uid}-no`}
                    className="input h-10 py-0 font-mono text-[13px]"
                    placeholder="forbidden"
                    spellCheck={false}
                    maxLength={200}
                    value={pb.response?.errorBody ?? ''}
                    onChange={(e) =>
                      update((d) => ((d.response ??= {}).errorBody = e.target.value))
                    }
                  />
                </MiniField>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
                <span className="text-stone">Common:</span>
                {(
                  [
                    ['ok', ''],
                    ['1', '0'],
                    ['OK', 'ERROR'],
                  ] as const
                ).map(([ok, no]) => {
                  const on =
                    (pb.response?.body ?? '') === ok && (pb.response?.errorBody ?? '') === no;
                  return (
                    <button
                      key={ok}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        update((d) => {
                          d.response = { ...(d.response ?? {}), body: ok, errorBody: no };
                        })
                      }
                      className={`h-8 rounded-full px-3 font-mono font-semibold transition ${on ? 'bg-ink text-white' : 'bg-ink/[0.06] text-graphite hover:bg-ink/10 hover:text-ink'}`}
                    >
                      {no ? `${ok} / ${no}` : ok}
                    </button>
                  );
                })}
              </div>
              <p className="rounded-[10px] bg-canvas px-3 py-2 text-[13px] leading-snug text-graphite">
                <span className="font-semibold text-ink">In short: </span>
                {`We answer “${pb.response?.body ?? ''}” when we take a postback or already have it, and “${pb.response?.errorBody || 'forbidden'}” (HTTP 403) when we refuse it, so the network knows whether to send it again.`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <PostbackUrl
        pb={pb}
        url={`${base}${path}`}
        providerId={providerId}
        saved={saved}
        onMacro={(id, v) =>
          update((d) => {
            d.macros = { ...(d.macros ?? {}) };
            if (v) d.macros[id] = v;
            else delete d.macros[id];
            if (!Object.keys(d.macros).length) delete d.macros;
          })
        }
      />
    </div>
  );
}

function MiniField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-[12px] font-semibold text-stone">
        {label}
      </label>
      {children}
    </div>
  );
}

function IpAllowlist({
  id,
  text,
  onText,
  hasKey,
  disabled = false,
}: {
  id: string;
  text: string;
  onText: (t: string) => void;
  hasKey: boolean;
  disabled?: boolean;
}) {
  const entries = splitAllowlist(text);
  const errors = entries.map(allowlistEntryError).filter((e): e is string => !!e);
  return (
    <Field
      id={id}
      label="Allowed IPs"
      hint={
        <>
          The network’s server IPs, from its postback documentation or support. Any format works:
          single IPs, CIDR (1.2.3.0/24), ranges (1.2.3.10-50) or wildcards (1.2.3.*), separated by
          commas, spaces or new lines.{' '}
          {entries.length === 0
            ? hasKey
              ? 'Leave empty to accept the key from any IP.'
              : 'Needed when the network can’t send a key.'
            : hasKey
              ? 'Calls must come from one of these IPs and carry the key.'
              : 'Calls from these IPs are accepted without a key.'}
        </>
      }
    >
      <textarea
        id={id}
        className="input h-24 font-mono text-[13px]"
        spellCheck={false}
        aria-invalid={errors.length > 0 || undefined}
        placeholder={'188.40.3.73\n157.90.97.0/24'}
        value={text}
        disabled={disabled}
        onChange={(e) => onText(e.target.value)}
      />
      {entries.length > 0 && errors.length === 0 && (
        <p className="mt-1.5 text-[12px] font-semibold text-mint-deep">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'} OK
        </p>
      )}
      {errors.map((e) => (
        <p key={e} className="mt-1.5 text-[12px] font-semibold text-berry">
          {e}
        </p>
      ))}
    </Field>
  );
}

const STYLES: { id: string; label: string; make: (p: string) => string }[] = [
  { id: 'brackets', label: '[USER_ID]', make: (p) => `[${p.toUpperCase()}]` },
  { id: 'braces', label: '{user_id}', make: (p) => `{${p}}` },
  { id: 'percent', label: '%USER_ID%', make: (p) => `%${p.toUpperCase()}%` },
  { id: 'double', label: '{{user_id}}', make: (p) => `{{${p}}}` },
];

/** "a|b" → "a"; dotted paths are JSON body fields, which can't be put in a URL. */
const firstParam = (ref: string) => ref.split('|')[0]!.trim();

/**
 * The full URL to paste into the network's postback settings: our endpoint, the shared key and
 * every mapped parameter set to the network's macro. Macros are saved with the provider.
 */
function PostbackUrl({
  pb,
  url,
  providerId,
  saved,
  onMacro,
}: {
  pb: Pb;
  url: string;
  providerId: string;
  saved: boolean;
  onMacro: (id: string, value: string) => void;
}) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const canReveal = useCan()('providers.secrets');
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fields = pb.fields ?? {};
  const macros = pb.macros ?? {};
  const style = STYLES.find((s) => s.id === macros._style) ?? STYLES[0]!;
  const key = pb.auth?.key;
  const masked = !!key?.value && key.value.startsWith('•');
  const keyValue = masked ? revealed : key?.value || null;

  const entries = [
    ...ROWS.filter((r) => typeof fields[r.key] === 'string' && fields[r.key]).map((r) => ({
      id: r.key as string,
      label: r.label,
      param: firstParam(fields[r.key] as string),
    })),
    ...(pb.reversal?.statusField
      ? [{ id: 'status', label: 'Status', param: firstParam(pb.reversal.statusField) }]
      : []),
    ...Object.entries(fields.extra ?? {}).map(([label, ref]) => ({
      id: `extra.${label}`,
      label,
      param: firstParam(ref),
    })),
  ];
  const inUrl = entries.filter((e) => !e.param.includes('.'));
  const nested = entries.length - inUrl.length;
  const bodySource = pb.source === 'json' || pb.source === 'form';

  const query = [
    ...(key?.location === 'query' && key.name ? [`${key.name}=${keyValue ?? 'YOUR_KEY'}`] : []),
    ...(bodySource ? [] : inUrl.map((e) => `${e.param}=${macros[e.id] || style.make(e.param)}`)),
  ];
  const full = query.length ? `${url}?${query.join('&')}` : url;

  const reveal = async () => {
    setErr(null);
    try {
      const r = await api<{ key: string | null }>(`/providers/${providerId}/key`);
      setRevealed(r.key);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(full);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <section
      aria-labelledby="pb-url-title"
      className="rounded-[16px] border-[1.5px] border-ink bg-paper p-4 shadow-[3px_3px_0_#111110] sm:p-5"
    >
      <h4 id="pb-url-title" className="text-[15px] font-bold">
        Postback URL for the network
      </h4>
      <p className="mt-0.5 text-[13px] text-stone">
        Paste this into the network’s postback (callback) settings.
      </p>

      {!bodySource && entries.length > 0 && (
        <>
          <div
            role="radiogroup"
            aria-label="Network macro style"
            className="mt-4 flex flex-wrap items-center gap-1.5"
          >
            <span className="mr-1 text-[12px] font-semibold text-stone">Macro style</span>
            {STYLES.map((s) => {
              const on = style.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onMacro('_style', s.id === 'brackets' ? '' : s.id)}
                  className={`inline-flex h-8 items-center rounded-full border px-3 font-mono text-[12px] transition ${on ? 'border-ink bg-ink text-white' : 'border-hairline bg-paper text-graphite hover:border-ink hover:text-ink'}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 divide-y divide-hairline rounded-[12px] border border-hairline">
            {inUrl.map((e) => (
              <div
                key={e.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 px-3 py-2 sm:grid-cols-[140px_130px_minmax(0,1fr)]"
              >
                <span className="text-[13px] font-semibold">{e.label}</span>
                <code className="truncate text-right text-[12px] text-stone sm:text-left sm:text-[13px] sm:text-graphite">
                  {e.param}=
                </code>
                <input
                  className="input col-span-2 h-9 py-0 font-mono text-[13px] sm:col-span-1"
                  placeholder={style.make(e.param)}
                  value={macros[e.id] ?? ''}
                  spellCheck={false}
                  onChange={(ev) => onMacro(e.id, ev.target.value.trim())}
                  aria-label={`Network macro for ${e.label}`}
                />
              </div>
            ))}
          </div>
          <p className="mt-2 text-[12px] leading-snug text-stone">
            A macro is the placeholder the network swaps for the real value. Type the network’s
            exact macro where it differs from the suggestion (e.g. AdBreak uses [YOUR_USER_ID] and
            [REWARD_VALUE]).
            {nested > 0 &&
              ` ${nested} nested JSON field${nested > 1 ? 's are' : ' is'} sent in the body, not the URL.`}
          </p>
        </>
      )}
      {bodySource && (
        <p className="mt-3 text-[13px] text-graphite">
          This network posts its data in the request body ({pb.source}), so the URL only needs the
          endpoint{key?.location === 'query' ? ' and key' : ''}.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <code className="block min-w-0 flex-1 break-all rounded-[10px] bg-canvas px-3 py-2.5 text-[12.5px] leading-relaxed text-ink">
          {full}
        </code>
        <button
          type="button"
          className="btn-primary h-11 shrink-0 px-4 py-0 sm:h-auto"
          onClick={copy}
          aria-live="polite"
        >
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}{' '}
          {copied ? 'Copied' : 'Copy URL'}
        </button>
      </div>
      {key?.location === 'header' && key.name && (
        <p className="mt-2 text-[12px] text-graphite">
          Also ask the network to send the header <code className="font-semibold">{key.name}</code>{' '}
          with your key.
        </p>
      )}
      {masked && !revealed && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-stone">
          The saved key is hidden, so the URL shows YOUR_KEY.
          {saved && canReveal && (
            <button
              type="button"
              onClick={reveal}
              className="-my-1 inline-flex items-center gap-1 rounded-[6px] px-1 py-1 font-semibold text-ink underline decoration-ink/30 underline-offset-2 transition hover:decoration-ink"
            >
              <Eye size={13} aria-hidden="true" /> Show the key in the URL
            </button>
          )}
        </p>
      )}
      {!key?.value && !pb.auth?.ipAllowlist?.length && (
        <p className="mt-3 flex items-start gap-2 rounded-[10px] bg-berry/[0.06] px-3 py-2 text-[12px] font-semibold text-berry-deep">
          <TriangleAlert size={14} className="mt-px shrink-0" aria-hidden="true" />
          Add a shared key (or allowed IPs) first: postbacks with neither are refused.
        </p>
      )}
      {err && (
        <p role="alert" className="mt-2 text-[12px] font-semibold text-berry-deep">
          {err}
        </p>
      )}
    </section>
  );
}
